import React, { useEffect, useMemo, useState } from 'react';
import { useCombobox } from 'downshift';

import { darculaTheme, darculaHyperDashboardStyles } from './darculaTheme';

const { routeSelectStyle, routeLabelStyle } = darculaHyperDashboardStyles;

const filterByQuery = (items, query) => {
    const q = (query ?? '').trim().toLowerCase();
    if (!q) {
        return items;
    }
    return items.filter(
        item =>
            item.label.toLowerCase().includes(q) ||
            String(item.value).toLowerCase().includes(q)
    );
};

const menuListStyle = {
    position: 'absolute',
    left: 0,
    right: 0,
    top: '100%',
    zIndex: 600,
    maxHeight: 'min(280px, 50vh)',
    overflowY: 'auto',
    marginTop: '4px',
    backgroundColor: darculaTheme.bgInput,
    color: darculaTheme.fg,
    border: `1px solid ${darculaTheme.border}`,
    borderRadius: '3px',
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.45)',
    listStyle: 'none',
    padding: 0,
    margin: 0,
};

const clearButtonStyle = {
    position: 'absolute',
    right: '4px',
    top: '50%',
    transform: 'translateY(-50%)',
    /** Fixed box so the glyph centers reliably (avoids FA / flex quirks). */
    width: '2rem',
    height: '2rem',
    padding: 0,
    border: 'none',
    backgroundColor: 'transparent',
    color: darculaTheme.fg,
    cursor: 'pointer',
    borderRadius: '4px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    lineHeight: 1,
    zIndex: 2,
    fontSize: '1.35rem',
    fontWeight: 300,
    fontFamily: 'system-ui, sans-serif',
};

const inputStyleBase = {
    ...routeSelectStyle,
    fontSize: '1.2rem',
    lineHeight: 1.35,
    minHeight: '40px',
    outline: 'none',
    caretColor: darculaTheme.lightPurple,
};

/**
 * Filterable combobox for HyperDashboard (e.g. route picker with many entries).
 */
const HyperDashboardAutocomplete = ({
    id,
    labelText,
    options,
    value,
    onValueChange,
    placeholder = 'Type to filter…',
    labelStyle = routeLabelStyle,
}) => {

    const [ isInputFocused, setIsInputFocused ] = useState(false);

    const selectedItem = useMemo(
        () => options.find(o => o.value === value) ?? null,
        [ options, value ]
    );

    const [ filteredItems, setFilteredItems ] = useState(() => filterByQuery(options, ''));

    useEffect(() => {
        setFilteredItems(filterByQuery(options, ''));
    }, [ options ]);

    const {
        isOpen,
        inputValue,
        getMenuProps,
        getInputProps,
        getItemProps,
        getLabelProps,
        highlightedIndex,
        setInputValue,
        openMenu,
    } = useCombobox({
        id,
        labelId: `${id}-label`,
        items: filteredItems,
        itemToString: item => (item ? item.label : ''),
        selectedItem,
        onSelectedItemChange: ({ selectedItem: nextItem }) => {
            if (nextItem) {
                onValueChange(nextItem.value);
            }
        },
        onInputValueChange: ({ inputValue: next }) => {
            setFilteredItems(filterByQuery(options, next ?? ''));
        },
    });

    useEffect(() => {
        setInputValue(selectedItem ? selectedItem.label : '');
    }, [ selectedItem, setInputValue ]);

    const showClear = (inputValue ?? '').length > 0;

    const handleClearClick = e => {
        e.preventDefault();
        e.stopPropagation();
        setInputValue('');
        setFilteredItems(filterByQuery(options, ''));
        openMenu();
    };

    const resolvedInputProps = getInputProps({
        onFocus: () => setIsInputFocused(true),
        onBlur: () => {
            setIsInputFocused(false);
            setFilteredItems(filterByQuery(options, ''));
            requestAnimationFrame(() => {
                setInputValue(selectedItem ? selectedItem.label : '');
            });
        },
    });

    return (
        <div style={{ position: 'relative', width: '100%' }}>
            <style>
                { `
                    #${id}::placeholder {
                        color: ${darculaTheme.fg};
                        opacity: 0.55;
                    }
                    #${id}:focus::placeholder {
                        opacity: 0.85;
                    }
                    .hyper-dashboard-autocomplete-clear-${id}:hover {
                        background-color: ${darculaTheme.button} !important;
                    }
                ` }
            </style>
            <label { ...getLabelProps() } style={ labelStyle }>
                { labelText }
            </label>
            <div style={{ position: 'relative', width: '100%' }}>
                <input
                    { ...resolvedInputProps }
                    id={ id }
                    title={ `${placeholder} (editable)` }
                    placeholder={ placeholder }
                    autoComplete="off"
                    autoCorrect="off"
                    spellCheck={ false }
                    style={{
                        ...inputStyleBase,
                        ...(showClear ? { paddingRight: '2.35rem' } : {}),
                        boxShadow: isInputFocused ?
                            `0 0 0 2px ${darculaTheme.borderAccent}` :
                            'none',
                        borderColor: isInputFocused ? darculaTheme.borderAccent : darculaTheme.border,
                        ...resolvedInputProps.style,
                    }}
                />
                {
                    showClear && (
                        <button
                            type="button"
                            className={ `hyper-dashboard-autocomplete-clear-${id}` }
                            tabIndex={ -1 }
                            aria-label="Clear field"
                            title="Clear"
                            style={ clearButtonStyle }
                            onMouseDown={ e => e.preventDefault() }
                            onClick={ handleClearClick }
                        >
                            <span aria-hidden style={{ position: 'relative', top: '-1px' }}>
                                &times;
                            </span>
                        </button>
                    )
                }
            </div>
            <ul { ...getMenuProps() } style={ menuListStyle }>
                {
                    isOpen &&
                    filteredItems.map((item, index) => (
                        <li
                            key={ item.optionId }
                            { ...getItemProps({ item, index }) }
                            style={{
                                padding: '8px 10px',
                                cursor: 'pointer',
                                backgroundColor: highlightedIndex === index ?
                                    darculaTheme.buttonPressed :
                                    'transparent',
                            }}
                        >
                            { item.label }
                        </li>
                    ))
                }
                {
                    isOpen && filteredItems.length === 0 && (
                        <li
                            style={{
                                padding: '8px 10px',
                                opacity: 0.75,
                                cursor: 'default',
                            }}
                        >
                            No matches
                        </li>
                    )
                }
            </ul>
        </div>
    );
};

export default HyperDashboardAutocomplete;
